/**
 * ConfirmDialog — a small in-app confirmation modal (replaces window.confirm,
 * which is banned house-wide). Presentational: the caller owns open state and
 * the confirm/cancel handlers.
 *
 * Usage:
 *   <ConfirmDialog
 *     open={!!target}
 *     title="Delete package?"
 *     message="This removes it permanently…"
 *     confirmLabel="Delete"
 *     danger
 *     busy={deleting}
 *     onConfirm={doDelete}
 *     onCancel={() => setTarget(null)}
 *   />
 */
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function ConfirmDialog({
  open,
  title = 'Are you sure?',
  message = '',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  danger = false,
  busy = false,
  onConfirm,
  onCancel,
  testid = 'confirm-dialog',
}) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          role="dialog"
          aria-modal="true"
          data-testid={testid}
          onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onCancel?.(); }}
        >
          <motion.div
            className="w-full max-w-md overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl"
            initial={{ scale: 0.98, y: 6 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.98, y: 6 }}
          >
            <div className="px-5 py-4">
              <h3 className="text-base font-semibold text-gray-900">{title}</h3>
              {message ? (
                <div className="mt-2 whitespace-pre-line text-sm leading-relaxed text-gray-600">{message}</div>
              ) : null}
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-5 py-3">
              {cancelLabel !== null ? (
                <button
                  type="button"
                  onClick={onCancel}
                  disabled={busy}
                  data-testid={`${testid}-cancel`}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-800 hover:bg-gray-50 disabled:opacity-50"
                >
                  {cancelLabel}
                </button>
              ) : null}
              <button
                type="button"
                onClick={onConfirm}
                disabled={busy}
                data-testid={`${testid}-confirm`}
                className={`rounded-lg px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50 ${
                  danger ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {busy ? 'Working…' : confirmLabel}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
